/**
 * Illustrative Case Studies for Ads Dashbound.
 *
 * IMPORTANT — Transparency note:
 * The metrics in these case studies are ILLUSTRATIVE composites built from
 * patterns we see across advertiser launches in each vertical. They are not
 * tied to a single named client. Every case-study page renders a visible
 * disclaimer to make this clear to readers.
 *
 * Why we still publish them: prospective advertisers need a realistic sense
 * of how campaigns are structured, what KPIs matter, and what scaling looks
 * like before they apply. These case studies provide that structure without
 * overclaiming.
 */
import { Home, CreditCard, ShieldCheck } from 'lucide-react';

export const CASE_STUDIES = [
  {
    slug: 'real-estate-developer-lead-gen-mena',
    title: 'Real Estate Developer — Lead Generation Across MENA',
    eyebrow: 'Case Study · Real Estate',
    short:
      'How a MENA-focused property developer reduced cost-per-qualified-lead by ~38% in 90 days by combining push, in-page push, and aggressive source-level controls.',
    icon: Home,
    accent: '#22D3EE',
    heroImage:
      'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80&auto=format&fit=crop',
    imageCaption: 'Modern residential property',
    industry: 'Real Estate · Property Development',
    geos: ['UAE', 'Saudi Arabia', 'Egypt', 'Qatar'],
    formats: ['push', 'in-page-push', 'direct-click'],
    duration: '90-day launch window',
    relatedVertical: { slug: 'real-estate', label: 'Real Estate' },
    summary:
      'A regional property developer launching a new mid-segment residential project across MENA needed qualified buyer leads with strict in-region targeting. Their previous self-serve buys produced volume but burned the agent team with out-of-region inquiries and recycled phone numbers.',
    challenge: [
      'Previous campaigns produced ~62% out-of-region leads that agents could not action.',
      'Cost-per-qualified-lead drifted above target band within the first two weeks of every scale-up.',
      'No reliable source-level visibility — placements were rotated by the network without notice.',
      'Compliance team needed strict claim controls (no “guaranteed” language, RERA-aligned disclosures).',
    ],
    approach: [
      {
        title: 'Funnel + format alignment',
        body: 'Started with push + in-page push only. Direct click was added in week three after the source whitelist stabilized. Pop was excluded for this advertiser to protect lead quality.',
      },
      {
        title: 'GEO + city tier-2 targeting',
        body: 'Country-level targeting was paired with landing-page level city qualifying questions, so the source data could feed back into network-side blacklists by GEO efficiency.',
      },
      {
        title: 'Source-level whitelist build',
        body: 'Week 1 ran wide. By day 8, the lowest-converting 40% of sources were blacklisted. By day 21, a tight whitelist of high-intent sources was driving the bulk of qualified inquiries.',
      },
      {
        title: 'Compliance + creative review',
        body: 'Every creative variant went through pre-flight compliance review for RERA-aligned language, accurate pricing bands, and honest project disclosures before going live.',
      },
    ],
    metrics: [
      { label: 'Qualified-lead share', from: '38%', to: '71%', note: 'Of total submitted leads' },
      { label: 'Cost per qualified lead', from: 'Baseline', to: '~38% lower', note: 'After 90-day window' },
      { label: 'In-region lead ratio', from: '38%', to: '92%', note: 'GEO-validated leads' },
      { label: 'Agent-confirmed contact rate', from: '21%', to: '47%', note: 'Reached + interested' },
    ],
    learnings: [
      'Pop was the wrong format for this funnel — protecting lead quality mattered more than raw volume.',
      'Daily source-level reviews in week one paid back across the full 90 days.',
      'Pre-flight compliance review eliminated mid-campaign creative rejections that previously cost days of momentum.',
    ],
    quote: {
      text: 'The difference was not the traffic source — it was that someone was actually reading source-level data in week one and acting on it.',
      attribution: 'Performance lead, MENA real-estate developer (illustrative composite)',
    },
  },

  {
    slug: 'credit-card-comparison-site-scaling',
    title: 'Credit Card Comparison Site — Scaling Approved-Card Volume',
    eyebrow: 'Case Study · Credit Cards',
    short:
      'How a credit-card comparison platform scaled approved-card volume ~3.4× over six months using soft-pull pre-qualification flows and curated source supply.',
    icon: CreditCard,
    accent: '#A78BFA',
    heroImage:
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1600&q=80&auto=format&fit=crop',
    imageCaption: 'Premium credit cards',
    industry: 'Fintech · Credit Card Comparison',
    geos: ['United States', 'Canada', 'United Kingdom'],
    formats: ['push', 'in-page-push', 'direct-click', 'domain-redirect'],
    duration: '6-month scaling cycle',
    relatedVertical: { slug: 'credit-cards', label: 'Finance — Credit Cards' },
    summary:
      'A credit-card comparison platform with strong organic traffic needed to scale paid acquisition without diluting their approval rate. Previous attempts via open self-serve networks hit a wall around $4K-$5K/day, where CPA climbed and approval rate dropped.',
    challenge: [
      'Approval rate from paid sources lagged organic by ~40% — making cost-per-approved unsustainable.',
      'Fraud / synthetic-identity submissions inflated raw lead numbers without producing funded cards.',
      'Self-serve placements rotated unpredictably, breaking source-level optimization.',
      'Compliance team needed APR / fee disclosures and no “guaranteed approval” language anywhere in the funnel.',
    ],
    approach: [
      {
        title: 'Soft-pull pre-qualification as the entry point',
        body: 'Replaced direct-application links with a soft-pull pre-qualification step. This pre-filtered users by card eligibility, dramatically improving approval rates for the issuer partners downstream.',
      },
      {
        title: 'Format split by card type',
        body: 'Travel + rewards cards routed via push and in-page push (engagement-heavy creatives). Cashback and starter cards routed via direct click and domain redirect (intent-heavy paths).',
      },
      {
        title: 'Fraud + source quality controls',
        body: 'Continuous bot filtering plus a whitelist-only model for the top 12 sources. Blacklist refresh ran weekly based on per-source approval rate, not raw submission volume.',
      },
      {
        title: 'Compliance review for every creative + landing page',
        body: 'APR, fees, and terms disclosed inside every funnel. Pre-flight legal review prevented the kind of mid-flight ad rejections that had previously cost the team scaling momentum.',
      },
    ],
    metrics: [
      { label: 'Approved-card volume', from: 'Baseline', to: '~3.4× growth', note: 'Over 6-month window' },
      { label: 'Per-source approval rate', from: '11%', to: '29%', note: 'On the top-performing whitelist' },
      { label: 'Fraud / duplicate submission ratio', from: '~9%', to: '<2%', note: 'After whitelist build' },
      { label: 'Cost per funded card', from: 'Baseline', to: '~28% lower', note: 'At higher monthly spend' },
    ],
    learnings: [
      'Soft-pull pre-qualification did more for downstream economics than any creative iteration.',
      'Approval-rate per source is the only metric that matters in credit-card lead gen — raw CPL is misleading.',
      'Curated, source-controlled supply was the difference between hitting a ceiling at $4K/day and scaling cleanly past $15K/day.',
    ],
    quote: {
      text: 'We were optimizing the wrong number for two years. The moment we switched the KPI from cost-per-lead to cost-per-approved-card, the scaling story rewrote itself.',
      attribution: 'Head of Acquisition, credit-card comparison platform (illustrative composite)',
    },
  },

  {
    slug: 'insurance-broker-quote-funnel-optimization',
    title: 'Insurance Broker — Multi-Product Quote Funnel Optimization',
    eyebrow: 'Case Study · Insurance',
    short:
      'How a multi-product insurance brokerage rebuilt quote economics across auto, home, and life — lifting bound-policy rate by ~52% with format-by-product routing.',
    icon: ShieldCheck,
    accent: '#34D399',
    heroImage:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&q=80&auto=format&fit=crop',
    imageCaption: 'Protection and coverage',
    industry: 'Insurance · Multi-Product Brokerage',
    geos: ['United States (licensed states)', 'United Kingdom', 'Australia'],
    formats: ['push', 'in-page-push', 'pop', 'direct-click'],
    duration: '120-day rebuild',
    relatedVertical: { slug: 'insurance', label: 'Insurance' },
    summary:
      'A multi-product brokerage was running auto, home, and life quote funnels under a single campaign budget — and getting blended results that hid the real winners and losers. Bound-policy rate was drifting, agent teams were burning hours on low-intent quote-shoppers, and quality varied wildly between sources.',
    challenge: [
      'Auto / home / life funnels were sharing creatives and source pools, blurring the data.',
      'Out-of-state traffic was entering licensed-region funnels and wasting agent time.',
      'Bound-policy rate per source was invisible — only aggregated quote volume was tracked.',
      'Quote-shopper density was high, particularly on auto, dragging down agent close rates.',
    ],
    approach: [
      {
        title: 'Product-split funnels with dedicated trackers',
        body: 'Separate creatives, separate landing pages, separate trackers for auto, home, and life. Source-level reporting then exposed exactly which placement converted best for each product.',
      },
      {
        title: 'Licensed-region GEO targeting',
        body: 'Source controls + GEO targeting locked traffic inside licensed states / countries. Out-of-region submissions dropped from a real cost into noise.',
      },
      {
        title: 'Bound-policy rate as the optimization KPI',
        body: 'Migrated source-level decisions away from cost-per-quote to cost-per-bound-policy. Blacklist refresh ran weekly using agent-confirmed close data, not network-reported volume.',
      },
      {
        title: 'Fraud + quote-shopper filtering',
        body: 'Continuous fraud filtering combined with source-level controls let the brokerage protect their agent team from the bottom 20% of placements that produced most of the noise.',
      },
    ],
    metrics: [
      { label: 'Bound-policy rate', from: '14%', to: '~52% lift', note: 'Quote → bound policy' },
      { label: 'Out-of-region quote ratio', from: '23%', to: '4%', note: 'Within licensed footprint' },
      { label: 'Agent close-rate on paid leads', from: '11%', to: '24%', note: 'After whitelist + fraud filters' },
      { label: 'Cost per bound policy', from: 'Baseline', to: '~33% lower', note: '120-day window' },
    ],
    learnings: [
      'A single insurance campaign should not optimize three product lines — split them.',
      'Cost-per-quote without bound-policy data is a vanity metric; agents see through it instantly.',
      'Licensed-region GEO discipline is non-negotiable for insurance; everything else flows from getting that right.',
    ],
    quote: {
      text: 'Splitting auto, home, and life into their own funnels did not just give us cleaner data — it gave us back our agent team’s time.',
      attribution: 'COO, multi-product insurance brokerage (illustrative composite)',
    },
  },
];

export const CASE_STUDIES_INDEX = {
  eyebrow: 'Case Studies',
  title: 'Let’s get you some business',
  intro:
    'Real-world performance marketing patterns from advertisers running curated, source-controlled traffic with Ads Dashbound. Each case study walks through the challenge, approach, KPIs, and what we learned — so you can map it onto your own funnel before applying.',
  disclaimer:
    'Note: Metrics shown are illustrative composites based on patterns we see across advertiser launches in each vertical. They are not tied to a single named client and should not be read as a promise of future performance.',
};
